/**
 * About — product-focused replacement (2026-05).
 *
 * Replaces the "single dad / DoorDash / 24-hour build" founder-story page
 * with a clean, professional, product-led description of SonSpeak. Same
 * URL (/about) so existing links keep working.
 *
 * Layout matches the rest of the new public surface:
 *   - HamburgerMenu sits above (mounted by AppRouter)
 *   - Logo hero
 *   - Three sections: What SonSpeak is · Who it's for · How it works
 *   - Single CTA to the waitlist
 */
import { Link } from "react-router-dom";
import { ArrowRight, Volume2, Users, Lock } from "lucide-react";
import { useSEO } from "@/lib/seo";

const VALUE_PROPS = [
  {
    icon: Volume2,
    title: "Communication, built for real life",
    body: "Symbol tiles, real photos, real voices, sentence builder, offline-capable. Designed for everyday use, not just clinic visits.",
  },
  {
    icon: Users,
    title: "For every adult in the child's life",
    body: "Parents, therapists, and teachers can edit the same child's board from any device. The child's tablet stays focused on communication.",
  },
  {
    icon: Lock,
    title: "Privacy-first",
    body: "Child names, photos, and voice recordings are never used for ads, never sold, and never used to train AI models.",
  },
];

export default function About() {
  useSEO({
    title: "About SonSpeak",
    description:
      "SonSpeak is an AAC (augmentative and alternative communication) app for nonverbal kids and adults — real voices, real photos, on any tablet.",
    path: "/about",
  });
  return (
    <div className="min-h-screen bg-[color:var(--cream)]">
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-14 text-center">
        <div className="flex justify-center mb-6">
          <img
            src="/brand/sonspeak-logo-transparent.png"
            alt="SonSpeak"
            className="w-full max-w-md sm:max-w-lg h-auto"
            data-testid="about-logo"
          />
        </div>
        <h1 className="display text-3xl sm:text-5xl font-extrabold leading-tight mb-4">
          A communication board your whole family can build together.
        </h1>
        <p className="text-base sm:text-lg text-[color:var(--ink)]/80 max-w-2xl mx-auto">
          SonSpeak helps nonverbal kids and adults speak with the voices of
          the people they love — real photos, real voices, on any tablet.
        </p>
      </section>

      <section className="bg-white border-t-[3px] border-[color:var(--ink)]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-14">
          <div className="grid sm:grid-cols-3 gap-5">
            {VALUE_PROPS.map((v) => (
              <div
                key={v.title}
                className="border-2 border-[color:var(--ink)]/15 rounded-lg p-5 bg-[color:var(--paper)]"
                data-testid={`about-value-${v.title.toLowerCase().replace(/\W+/g, "-")}`}
              >
                <v.icon size={22} className="mb-2 text-[color:var(--coral)]" aria-hidden />
                <h3 className="font-extrabold text-base mb-1">{v.title}</h3>
                <p className="text-sm text-[color:var(--ink)]/75 leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-10 sm:py-14 text-center">
        <h2 className="display text-2xl sm:text-3xl font-extrabold mb-3">
          Get notified when SonSpeak opens
        </h2>
        <p className="text-sm text-[color:var(--ink)]/70 mb-6">
          We're rolling out to a small early-access group while pricing and
          plans get finalized. Join the waitlist to be first in.
        </p>
        <div className="flex justify-center">
          <Link
            to="/waitlist"
            className="inline-flex items-center gap-2 bg-[color:var(--coral)] hover:bg-[color:var(--coral-deep)] text-white font-bold text-base px-6 py-3 rounded-md transition"
            data-testid="about-waitlist"
          >
            Join the waitlist <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
